import {
  Battery,
  ChevronLeft,
  ChevronRight,
  Monitor,
  Signal,
  Smartphone,
  Wifi,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import logoColor from '@/assets/brand/signcare-logo.svg';
import { cn } from '@/lib/utils';

import { useGoogleFont } from '../hooks/useGoogleFont';
import { type CustomizationSettings, toBrandCssVars } from '../schemas/customizationSchemas';

// Live preview of the applicant journey in a phone + browser frame. The SAME
// branding drives both — the tab only switches the device chrome (this is why
// there is one config, not two). Colours/radii/font come in as `--brand-*` CSS
// variables on each frame root; the markup reads var(--brand-*). Ported 1:1
// from the designer's .sc-sim-* pixel spec.
interface BrandPreviewProps {
  settings: CustomizationSettings;
  logoUrl: string | null;
}

type Device = 'mobile' | 'desktop';

// A tiny CSS-drawn Indian flag (matches the designer's inline flag).
function FlagIcon() {
  return (
    <div className="mr-1.5 flex h-[11px] w-4 shrink-0 flex-col overflow-hidden rounded-[1px] border border-grayEF">
      <div className="flex-1 bg-[#FF9933]" />
      <div className="flex flex-1 items-center justify-center bg-white">
        <div className="h-[2px] w-[2px] rounded-full bg-[#000080]" />
      </div>
      <div className="flex-1 bg-[#138808]" />
    </div>
  );
}

export function BrandPreview({ settings, logoUrl }: BrandPreviewProps) {
  const [device, setDevice] = useState<Device>('mobile');
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Load the picked Google Font so the typography change is visible live.
  useGoogleFont(settings.typographyStyle);

  // Fit the fixed-size frame (375×812 phone / 600×480 browser) into whatever
  // space the preview pane has, mirroring the designer's ResizeObserver.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      const { width, height } = rect;
      const [w, h] = device === 'mobile' ? [375, 830] : [600, 480];
      const next = Math.min((width - 40) / w, (height - 40) / h, 1);
      setScale(Math.max(next, 0.1));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [device]);

  const brandVars = toBrandCssVars(settings);
  const logo = logoUrl || logoColor;

  return (
    <div
      ref={containerRef}
      className="relative flex h-full min-h-0 w-full flex-1 flex-col items-center gap-4 overflow-hidden rounded-lg border border-grayEF bg-white p-4 shadow-[inset_0_2px_8px_rgba(0,0,0,0.01)]"
      style={{
        backgroundSize: '50px 50px',
        backgroundImage:
          'linear-gradient(to right, rgba(229,231,235,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(229,231,235,0.4) 1px, transparent 1px)',
      }}
    >
      {/* Device toggle */}
      <div className="flex items-center self-start rounded-lg bg-[#F9F9F9] p-1">
        <DeviceTab
          active={device === 'mobile'}
          onClick={() => setDevice('mobile')}
          icon={<Smartphone size={16} />}
          label="Mobile"
        />
        <DeviceTab
          active={device === 'desktop'}
          onClick={() => setDevice('desktop')}
          icon={<Monitor size={16} />}
          label="Desktop"
        />
      </div>

      {/* Scaled frame */}
      <div className="flex min-h-0 flex-1 items-center justify-center">
        {device === 'mobile' ? (
          // Match the designer: scale ONLY the phone; render the pagination
          // below it at normal size (so the phone's reserved height is exact
          // and it doesn't overflow the pane).
          <div className="flex flex-col items-center gap-5">
            <div
              style={{ width: 375 * scale * 0.85, height: 812 * scale * 0.85 }}
              className="relative shrink-0"
            >
              <div
                style={{ transform: `scale(${scale * 0.85})`, transformOrigin: 'top left' }}
                className="absolute left-0 top-0"
              >
                <PhoneFrame brandVars={brandVars} logo={logo} font={settings.typographyStyle} />
              </div>
            </div>
            <Pagination />
          </div>
        ) : (
          <div
            style={{ width: 600 * scale * 0.95, height: 480 * scale * 0.95 }}
            className="relative shrink-0"
          >
            <div
              style={{ transform: `scale(${scale * 0.95})`, transformOrigin: 'top left' }}
              className="absolute left-0 top-0"
            >
              <BrowserFrame brandVars={brandVars} logo={logo} font={settings.typographyStyle} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DeviceTab({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors',
        active ? 'bg-primary text-white' : 'bg-transparent text-[#64748b]',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// Shared inner journey content (phone number screen). Reads var(--brand-*).
function JourneyContent({ logo }: { logo: string }) {
  return (
    <>
      <div className="mb-6 flex h-9 w-full items-center justify-center">
        <img src={logo} alt="Brand" className="max-h-full max-w-[130px] object-contain" />
      </div>

      <div className="mb-6 w-full text-center">
        <h2
          className="m-0 mb-1.5 text-[20px] font-bold leading-tight"
          style={{ color: 'var(--brand-heading)' }}
        >
          Enter your phone number
        </h2>
        <p className="m-0 text-sm leading-snug" style={{ color: 'var(--brand-body)' }}>
          We&apos;ll send a code to verify it&apos;s you
        </p>
      </div>

      <div className="flex w-full flex-col gap-2">
        <span className="text-sm font-semibold" style={{ color: 'var(--brand-input-label)' }}>
          Phone Number
        </span>
        <div
          className="flex h-11 w-full items-center px-3.5"
          style={{
            backgroundColor: 'var(--brand-input-fill)',
            border: '1px solid var(--brand-input-stroke)',
            borderRadius: 'var(--brand-input-radius)',
          }}
        >
          <div
            className="mr-2.5 flex h-6 shrink-0 items-center pr-2.5"
            style={{ borderRight: '1px solid var(--brand-input-stroke)' }}
          >
            <FlagIcon />
            <span className="text-sm font-medium" style={{ color: 'var(--brand-heading)' }}>
              +91
            </span>
          </div>
          <span className="text-sm" style={{ color: 'var(--brand-input-placeholder)' }}>
            Enter your phone number
          </span>
        </div>
      </div>
    </>
  );
}

function StartButton() {
  return (
    <button
      type="button"
      className="flex h-11 w-full items-center justify-center text-sm font-semibold"
      style={{
        backgroundColor: 'var(--brand-btn-fill)',
        color: 'var(--brand-btn-text)',
        border: '1px solid var(--brand-btn-stroke)',
        borderRadius: 'var(--brand-btn-radius)',
      }}
    >
      Start Verification
    </button>
  );
}

function Pagination({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center justify-center gap-2', className)}>
      <button
        type="button"
        disabled
        className="flex h-[26px] w-[26px] items-center justify-center rounded-[6px] border border-[#f3f4f6] bg-white text-placeholder"
      >
        <ChevronLeft size={16} />
      </button>
      <div className="h-[5px] w-3 rounded-[3px] bg-[#1e293b]" />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="h-[5px] w-[5px] rounded-full bg-placeholder" />
      ))}
      <button
        type="button"
        disabled
        className="flex h-[26px] w-[26px] items-center justify-center rounded-[6px] border border-[#f3f4f6] bg-white text-placeholder"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

function PhoneFrame({
  brandVars,
  logo,
  font,
}: {
  brandVars: React.CSSProperties;
  logo: string;
  font: string;
}) {
  return (
    <div
      className="brand-scope relative flex h-[812px] w-[375px] select-none flex-col overflow-hidden rounded-[44px] border-[12px] border-[#0f172a] bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.18)]"
      style={{ ...brandVars, fontFamily: `"${font}", sans-serif` }}
    >
      <div className="absolute left-1/2 top-[10px] z-20 h-[22px] w-[90px] -translate-x-1/2 rounded-[11px] bg-[#0f172a]" />
      <div className="z-10 flex items-center justify-between px-6 pb-1.5 pt-4 text-[11px] font-semibold text-black">
        <span>3:22 PM</span>
        <div className="flex items-center gap-1">
          <Signal size={14} strokeWidth={2.5} />
          <Wifi size={14} strokeWidth={2.5} />
          <Battery size={16} strokeWidth={2.5} />
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center px-5 pb-5 pt-6">
        <JourneyContent logo={logo} />
      </div>

      <div className="w-full px-5 pb-4">
        <StartButton />
      </div>
    </div>
  );
}

function BrowserFrame({
  brandVars,
  logo,
  font,
}: {
  brandVars: React.CSSProperties;
  logo: string;
  font: string;
}) {
  return (
    <div
      className="brand-scope relative flex h-[480px] w-[600px] select-none flex-col overflow-hidden rounded-[24px] border-[8px] border-[#0f172a] bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.18)]"
      style={{ ...brandVars, fontFamily: `"${font}", sans-serif` }}
    >
      <div className="flex shrink-0 items-center gap-4 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full bg-[#ef4444]" />
          <div className="h-3 w-3 rounded-full bg-[#f59e0b]" />
          <div className="h-3 w-3 rounded-full bg-[#22c55e]" />
        </div>
        <div className="flex-1 rounded-md border border-grayEF bg-white px-3 py-1.5 text-center text-[11px] font-medium text-[#64748b]">
          https://verify.signcare.com/session/auth_smp901
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto bg-white p-8">
        <div className="flex w-[400px] flex-col items-center rounded-2xl border border-grayEF bg-white p-8 shadow-sm">
          <JourneyContent logo={logo} />
          <div className="mt-6 w-full">
            <StartButton />
          </div>
        </div>
        <Pagination className="mt-6" />
      </div>
    </div>
  );
}
